import { CSSProperties, HTMLAttributes, ReactNode, forwardRef } from "react";
import { theme } from "antd";
import { GlobalToken } from "antd/es/theme/interface";
import styles from "./index.module.css";

export type TagTone =
	| "neutral"
	| "primary"
	| "success"
	| "warning"
	| "danger"
	| "info";

export type TagVariant = "soft" | "outline";

export type TagProps = HTMLAttributes<HTMLSpanElement> & {
	tone?: TagTone;
	/** `soft` is a tinted pill for statuses; `outline` is a quiet chip for plain values. */
	variant?: TagVariant;
	icon?: ReactNode;
	/** Adds hover feedback, for tags that open a popover or act as a link. */
	interactive?: boolean;
};

// [background, text, hover background, border]
const getToneColors = (token: GlobalToken, tone: TagTone) =>
	({
		neutral: [
			token.colorFillTertiary,
			token.colorText,
			token.colorFillSecondary,
			token.colorBorder,
		],
		primary: [
			token.colorPrimaryBg,
			token.colorPrimaryText,
			token.colorPrimaryBgHover,
			token.colorPrimaryBorder,
		],
		success: [
			token.colorSuccessBg,
			token.colorSuccessText,
			token.colorSuccessBgHover,
			token.colorSuccessBorder,
		],
		warning: [
			token.colorWarningBg,
			token.colorWarningText,
			token.colorWarningBgHover,
			token.colorWarningBorder,
		],
		danger: [
			token.colorErrorBg,
			token.colorErrorText,
			token.colorErrorBgHover,
			token.colorErrorBorder,
		],
		info: [
			token.colorInfoBg,
			token.colorInfoText,
			token.colorInfoBgHover,
			token.colorInfoBorder,
		],
	}[tone]);

// forwardRef so antd Popover/Tooltip can attach to it
const Tag = forwardRef<HTMLSpanElement, TagProps>(
	(
		{
			tone = "neutral",
			variant = "soft",
			icon,
			interactive,
			className,
			style,
			children,
			...rest
		},
		ref,
	) => {
		const { token } = theme.useToken();
		const [bg, color, hoverBg, border] = getToneColors(token, tone);
		const cssVars = {
			"--tag-bg": bg,
			"--tag-color": color,
			"--tag-hover-bg": hoverBg,
			"--tag-border": border,
			"--tag-radius": `${token.borderRadiusSM}px`,
		} as CSSProperties;

		return (
			<span
				ref={ref}
				className={[
					styles.tag,
					styles[variant],
					interactive && styles.interactive,
					className,
				]
					.filter(Boolean)
					.join(" ")}
				style={{ ...cssVars, ...style }}
				// Popover/Tooltip inject their mouse/focus handlers as props
				{...rest}
			>
				{icon && <span className={styles.icon}>{icon}</span>}
				{children}
			</span>
		);
	},
);

Tag.displayName = "Tag";

export default Tag;
